import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Send, Settings, Shield, Plus, Mail, Folder } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { emailService } from '../services';
import type { EmailSearchFilters } from '../types/email';
import PageHeader from '../components/ui/PageHeader';
import { Button, Card } from '../components/ui';
import EmailList from '../components/email/EmailList';
import SentEmailsList from '../components/email/SentEmailsList';
import EmailCredentialsManager from '../components/email/EmailCredentialsManager';
import WhiteBlackListManager from '../components/email/WhiteBlackListManager';
import FolderManager from '../components/email/FolderManager';
import EmailComposer from '../components/email/EmailComposer';

function EmailManagement() {
  const [filters, setFilters] = useState<EmailSearchFilters>({
    page: 1,
    limit: 20,
    sort_direction: 'desc',
  });

  const [showComposer, setShowComposer] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox');
  const user = useAuthStore(state => state.user);

  // Get credentials for filters
  const { data: credentials } = useQuery({
    queryKey: ['email-credentials'],
    queryFn: () => emailService.getCredentials(),
  });

  // Get emails with filters
  const { data: emailsResponse, isLoading: isLoadingEmails } = useQuery({
    queryKey: ['emails', filters],
    queryFn: () => emailService.searchEmails(filters),
  });

  const tabs = [
    {
      id: 'inbox',
      label: 'Caixa de Entrada',
      Icon: Inbox,
      description: 'Visualize e gerencie seus emails recebidos',
    },
    {
      id: 'sent',
      label: 'Enviados',
      Icon: Send,
      description: 'Acompanhe seus emails enviados',
    },
    {
      id: 'folders',
      label: 'Pastas',
      Icon: Folder,
      description: 'Gerencie suas pastas de email',
    },
    // Only show credentials tab for parent accounts (not child accounts)
    ...(!user?.isChildAccount
      ? [
          {
            id: 'credentials',
            label: 'Credenciais',
            Icon: Settings,
            description: 'Configure suas contas de email',
          },
        ]
      : []),
    {
      id: 'whitelist',
      label: 'White/Black List',
      Icon: Shield,
      description: 'Gerencie as listas de permissão e bloqueio',
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'inbox':
        return (
          <EmailList
            emails={emailsResponse?.dados || []}
            isLoading={isLoadingEmails}
            filters={filters}
            onFiltersChange={setFilters}
            pagination={emailsResponse?.paginacao}
            credentials={credentials}
            showCredentialFilter={true}
          />
        );
      case 'sent':
        return <SentEmailsList credentials={credentials} showCredentialFilter={true} />;
      case 'folders':
        return <FolderManager />;
      case 'credentials':
        return <EmailCredentialsManager />;
      case 'whitelist':
        return <WhiteBlackListManager />;
      default:
        return null;
    }
  };

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Gerenciamento de E-mails"
          description="Gerencie seus e-mails, credenciais e listas de permissão."
          actions={
            <Button onClick={() => setShowComposer(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Novo E-mail
            </Button>
          }
        />

        {/* Navigation Tabs */}
        <div className="mt-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 text-sm font-medium
                    ${
                      isActive
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  <tab.Icon
                    className={`h-5 w-5 ${isActive ? 'text-primary-500' : 'text-gray-400'} mr-2`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                  {activeTabData && (
                    <activeTabData.Icon className="h-5 w-5 mr-2 text-primary-500" />
                  )}
                  {activeTabData?.label}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {activeTabData?.description}
                </p>
              </div>
            </div>

            {renderTabContent()}
          </Card>
        </div>

        <EmailComposer
          isOpen={showComposer}
          onClose={() => setShowComposer(false)}
          credentials={credentials}
        />
      </div>
    </div>
  );
}

export default EmailManagement;
